import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit3, Mail, FileText, CheckSquare, Webhook } from 'lucide-react';
import { createPersistedMockData } from '../mockDataManager';

// ============================================
// TYPES
// ============================================

export type WorkflowTrigger =
  | 'created'              // Record is created
  | 'updated'              // Record is updated
  | 'created_or_updated'   // Record is created or updated
  | 'field_changed';       // Specific field(s) changed

export type WorkflowActionType =
  | 'update_fields'    // Update field values
  | 'send_email'       // Send email notification
  | 'create_record'    // Create a new record
  | 'create_task'      // Create a task
  | 'call_webhook';    // Call external API

export interface WorkflowCondition {
  id: string;
  field: string;
  fieldLabel: string;
  operator: 'equals' | 'notEquals' | 'lessThan' | 'greaterThan' | 'contains' | 'startsWith' | 'isBlank' | 'isNotBlank';
  value: string | number | boolean;
}

// Field Mapping types for advanced workflow actions
export type FieldMappingType = 'static' | 'formula' | 'related_field' | 'user_field' | 'record_field';

export interface WorkflowFieldMapping {
  id: string;
  targetField: string;
  targetFieldLabel: string;
  mappingType: FieldMappingType;
  // For static values
  staticValue?: string | number | boolean;
  // For formulas
  formula?: string;
  // For related record fields
  relationshipPath?: string;
  relatedField?: string;
  relatedFieldLabel?: string;
  // For user fields
  userFieldType?: 'current_user' | 'record_owner' | 'specific_user';
  specificUserId?: string;
  // For record fields
  recordField?: string;
  recordFieldLabel?: string;
}

export interface WorkflowAction {
  id: string;
  type: WorkflowActionType;
  order: number;

  // For update_fields
  fieldUpdates?: Array<{
    field: string;
    fieldLabel: string;
    value: string | number | boolean;
    useFormula?: boolean;
  }>;

  // For send_email
  emailTemplate?: string;
  emailRecipients?: string[];
  emailSubject?: string;
  emailBody?: string;

  // For create_record
  relatedObject?: string;
  relatedObjectLabel?: string;
  recordFields?: Array<{
    field: string;
    fieldLabel: string;
    value: string | number | boolean;
  }>;
  // Advanced field mappings for create_record (PE-776)
  fieldMappings?: WorkflowFieldMapping[];

  // For create_task
  taskSubject?: string;
  taskDescription?: string;
  taskDueDate?: string;
  taskAssignedTo?: string;
  taskPriority?: 'High' | 'Medium' | 'Low';

  // For call_webhook
  webhookUrl?: string;
  webhookMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  webhookHeaders?: string; // JSON string of headers
  webhookBody?: string;
}

export interface WorkflowRule {
  id: string;
  name: string;
  description?: string;
  objectName: string;
  objectLabel: string;

  // Trigger configuration
  trigger: WorkflowTrigger;
  triggeredFields?: string[]; // For field_changed trigger

  // Condition configuration
  hasConditions: boolean;
  conditions: WorkflowCondition[];
  conditionLogic: 'AND' | 'OR';

  // Actions
  actions: WorkflowAction[];

  // Settings
  isActive: boolean;
  executionOrder: number;
  sendErrorNotifications: boolean;

  // Metadata
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
  lastRunDate?: string;
  executionCount: number;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName: string;
  recordId: string;
  recordName: string;
  status: 'success' | 'failed' | 'partial';
  startTime: string;
  endTime: string;
  duration: number;
  actionsExecuted: number;
  actionsFailed: number;
  errorMessage?: string;
}

// ============================================
// MOCK DATA
// ============================================

const defaultMockWorkflowRules: WorkflowRule[] = [
  // Workflow 1: Send Welcome Email (Client)
  {
    id: 'wf_Client_c_001',
    name: 'Send Welcome Email',
    description: 'Automatically send welcome email to new clients',
    objectName: 'Client__c',
    objectLabel: 'Client',
    trigger: 'created',
    triggeredFields: [],
    hasConditions: true,
    conditions: [
      {
        id: 'cond_001',
        field: 'client_status__c',
        fieldLabel: 'Client Status',
        operator: 'equals',
        value: 'New'
      },
      {
        id: 'cond_002',
        field: 'email__c',
        fieldLabel: 'Email',
        operator: 'isNotBlank',
        value: ''
      }
    ],
    conditionLogic: 'AND',
    actions: [
      {
        id: 'action_001',
        type: 'send_email',
        order: 1,
        emailTemplate: 'Welcome Email Template',
        emailRecipients: ['{Client.Email}'],
        emailSubject: 'Welcome to Our Service!',
        emailBody: 'Hi {Client.Name}, welcome to our platform...'
      },
      {
        id: 'action_002',
        type: 'update_fields',
        order: 2,
        fieldUpdates: [
          {
            field: 'client_status__c',
            fieldLabel: 'Client Status',
            value: 'Contacted',
            useFormula: false
          },
          {
            field: 'last_contact_date__c',
            fieldLabel: 'Last Contact Date',
            value: 'TODAY()',
            useFormula: true
          }
        ]
      },
      {
        id: 'action_003',
        type: 'create_task',
        order: 3,
        taskSubject: 'Follow up with {Client.Name}',
        taskDescription: 'Initial follow-up call',
        taskDueDate: 'TODAY() + 7',
        taskPriority: 'High'
      }
    ],
    isActive: true,
    executionOrder: 1,
    sendErrorNotifications: true,
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin',
    lastRunDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    executionCount: 47
  },

  // Workflow 2: Mark High Value Client
  {
    id: 'wf_Client_c_002',
    name: 'Mark High Value Client',
    description: 'Automatically flag clients with high annual revenue',
    objectName: 'Client__c',
    objectLabel: 'Client',
    trigger: 'field_changed',
    triggeredFields: ['annual_revenue__c'],
    hasConditions: true,
    conditions: [
      {
        id: 'cond_003',
        field: 'annual_revenue__c',
        fieldLabel: 'Annual Revenue',
        operator: 'greaterThan',
        value: 100000
      }
    ],
    conditionLogic: 'AND',
    actions: [
      {
        id: 'action_004',
        type: 'update_fields',
        order: 1,
        fieldUpdates: [
          {
            field: 'client_status__c',
            fieldLabel: 'Client Status',
            value: 'VIP',
            useFormula: false
          },
          {
            field: 'priority__c',
            fieldLabel: 'Priority',
            value: 'High',
            useFormula: false
          }
        ]
      },
      {
        id: 'action_005',
        type: 'create_task',
        order: 2,
        taskSubject: 'Review high-value client: {Client.Name}',
        taskDescription: 'Schedule account review meeting',
        taskDueDate: 'TODAY() + 3',
        taskPriority: 'High'
      }
    ],
    isActive: true,
    executionOrder: 2,
    sendErrorNotifications: true,
    createdBy: 'Sales Manager',
    createdDate: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Sales Manager',
    lastRunDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    executionCount: 12
  },

  // Workflow 3: Auto-Archive Old Clients (Inactive)
  {
    id: 'wf_Client_c_003',
    name: 'Auto-Archive Old Clients',
    description: 'Archive clients with no activity for over a year',
    objectName: 'Client__c',
    objectLabel: 'Client',
    trigger: 'updated',
    triggeredFields: [],
    hasConditions: true,
    conditions: [
      {
        id: 'cond_004',
        field: 'last_activity__c',
        fieldLabel: 'Last Activity',
        operator: 'lessThan',
        value: 'TODAY() - 365'
      }
    ],
    conditionLogic: 'AND',
    actions: [
      {
        id: 'action_006',
        type: 'update_fields',
        order: 1,
        fieldUpdates: [
          {
            field: 'client_status__c',
            fieldLabel: 'Client Status',
            value: 'Archived',
            useFormula: false
          }
        ]
      }
    ],
    isActive: false,
    executionOrder: 3,
    sendErrorNotifications: false,
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin',
    executionCount: 0
  },

  // Workflow 4: Webhook Notification
  {
    id: 'wf_Client_c_004',
    name: 'Notify External System',
    description: 'Send client data to external CRM when created',
    objectName: 'Client__c',
    objectLabel: 'Client',
    trigger: 'created_or_updated',
    triggeredFields: [],
    hasConditions: false,
    conditions: [],
    conditionLogic: 'AND',
    actions: [
      {
        id: 'action_007',
        type: 'call_webhook',
        order: 1,
        webhookUrl: 'https://api.example.com/crm/clients',
        webhookMethod: 'POST',
        webhookHeaders: JSON.stringify({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer xxx'
        }),
        webhookBody: JSON.stringify({
          id: '{Client.Id}',
          name: '{Client.Name}',
          email: '{Client.Email}'
        })
      }
    ],
    isActive: true,
    executionOrder: 4,
    sendErrorNotifications: true,
    createdBy: 'Admin',
    createdDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastModifiedBy: 'Admin',
    lastRunDate: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    executionCount: 23
  }
];

export const mockWorkflowExecutions: WorkflowExecution[] = [
  {
    id: 'exec_001',
    workflowId: 'wf_Client_c_001',
    workflowName: 'Send Welcome Email',
    recordId: 'client_001',
    recordName: 'Acme Corporation',
    status: 'success',
    startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() - 2 * 60 * 60 * 1000 + 2000).toISOString(),
    duration: 2000,
    actionsExecuted: 3,
    actionsFailed: 0
  },
  {
    id: 'exec_002',
    workflowId: 'wf_Client_c_002',
    workflowName: 'Mark High Value Client',
    recordId: 'client_002',
    recordName: 'TechCorp Industries',
    status: 'success',
    startTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() - 24 * 60 * 60 * 1000 + 1500).toISOString(),
    duration: 1500,
    actionsExecuted: 2,
    actionsFailed: 0
  },
  {
    id: 'exec_003',
    workflowId: 'wf_Client_c_001',
    workflowName: 'Send Welcome Email',
    recordId: 'client_003',
    recordName: 'Global Services LLC',
    status: 'failed',
    startTime: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() - 12 * 60 * 60 * 1000 + 800).toISOString(),
    duration: 800,
    actionsExecuted: 1,
    actionsFailed: 2,
    errorMessage: 'Failed to send email: Invalid email address'
  },
  {
    id: 'exec_004',
    workflowId: 'wf_Client_c_004',
    workflowName: 'Notify External System',
    recordId: 'client_004',
    recordName: 'Enterprise Solutions Inc',
    status: 'partial',
    startTime: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() - 6 * 60 * 60 * 1000 + 3000).toISOString(),
    duration: 3000,
    actionsExecuted: 1,
    actionsFailed: 0,
    errorMessage: 'Webhook responded with status 500'
  }
];

// Create persisted workflows using centralized mock data manager
const persistedWorkflows = createPersistedMockData('crm_workflows', defaultMockWorkflowRules);

// Export for backwards compatibility
export const mockWorkflowRules = persistedWorkflows.data;

// ============================================
// QUERY KEYS
// ============================================

export const workflowKeys = {
  all: ['workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  list: (objectName?: string) => [...workflowKeys.lists(), objectName] as const,
  details: () => [...workflowKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
  executions: (workflowId?: string) => [...workflowKeys.all, 'executions', workflowId] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all workflow rules
 */
export function useWorkflowRules(
  objectName?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: workflowKeys.list(objectName),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 400));

      const workflows = persistedWorkflows.get();

      if (objectName) {
        return workflows.filter(wf => wf.objectName === objectName);
      }

      return workflows;
    },
    staleTime: 2 * 60 * 1000,
    ...(options?.enabled !== undefined && { enabled: options.enabled }),
  });
}

/**
 * Get single workflow rule
 */
export function useWorkflowRule(workflowId: string) {
  return useQuery({
    queryKey: workflowKeys.detail(workflowId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const workflow = persistedWorkflows.get().find(wf => wf.id === workflowId);
      if (!workflow) throw new Error('Workflow not found');

      return workflow;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!workflowId,
  });
}

/**
 * Get workflow executions
 */
export function useWorkflowExecutions(workflowId?: string) {
  return useQuery({
    queryKey: workflowKeys.executions(workflowId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      if (workflowId) {
        return mockWorkflowExecutions.filter(exec => exec.workflowId === workflowId);
      }

      return mockWorkflowExecutions;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Create workflow rule
 */
export function useCreateWorkflowRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowData: Omit<WorkflowRule, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy' | 'executionCount'>) => {
      await new Promise(resolve => setTimeout(resolve, 600));

      const newWorkflow: WorkflowRule = {
        ...workflowData,
        id: `wf_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
        executionCount: 0
      };

      return persistedWorkflows.add(newWorkflow);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() }); // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
    },
  });
}

/**
 * Update workflow rule
 */
export function useUpdateWorkflowRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workflowId, updates }: { workflowId: string; updates: Partial<WorkflowRule> }) => {
      await new Promise(resolve => setTimeout(resolve, 500));

      const finalUpdates = {
        ...updates,
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User'
      };

      const updated = persistedWorkflows.update(workflowId, finalUpdates);
      if (!updated) throw new Error('Workflow not found');

      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() }); // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
    },
  });
}

/**
 * Delete workflow rule
 */
export function useDeleteWorkflowRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workflowId, objectName }: { workflowId: string; objectName: string }) => {
      await new Promise(resolve => setTimeout(resolve, 400));

      const success = persistedWorkflows.remove(workflowId);
      if (!success) throw new Error('Workflow not found');

      return { workflowId, objectName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() }); // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
    },
  });
}

/**
 * Clone workflow rule
 */
export function useCloneWorkflowRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));

      const workflow = persistedWorkflows.get().find(wf => wf.id === workflowId);
      if (!workflow) throw new Error('Workflow not found');

      const clonedWorkflow: WorkflowRule = {
        ...workflow,
        id: `wf_${Date.now()}`,
        name: `${workflow.name} (Copy)`,
        isActive: false,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User',
        executionCount: 0,
        lastRunDate: undefined
      };

      return persistedWorkflows.add(clonedWorkflow);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() }); // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
    },
  });
}

/**
 * Test workflow rule
 */
export function useTestWorkflowRule() {
  return useMutation({
    mutationFn: async ({ workflowId, recordId }: { workflowId: string; recordId: string }) => {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const workflow = persistedWorkflows.get().find(wf => wf.id === workflowId);
      if (!workflow) throw new Error('Workflow not found');

      // Simulate test execution
      const success = Math.random() > 0.2; // 80% success rate

      const testResult: WorkflowExecution = {
        id: `exec_test_${Date.now()}`,
        workflowId: workflow.id,
        workflowName: workflow.name,
        recordId,
        recordName: 'Test Record',
        status: success ? 'success' : 'failed',
        startTime: new Date().toISOString(),
        endTime: new Date(Date.now() + 2000).toISOString(),
        duration: 2000,
        actionsExecuted: success ? workflow.actions.length : Math.floor(workflow.actions.length / 2),
        actionsFailed: success ? 0 : Math.ceil(workflow.actions.length / 2),
        errorMessage: success ? undefined : 'Test execution failed: Sample error message'
      };

      return testResult;
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get trigger label
 */
export function getTriggerLabel(trigger: WorkflowTrigger): string {
  const labels: Record<WorkflowTrigger, string> = {
    created: 'Record Created',
    updated: 'Record Updated',
    created_or_updated: 'Record Created or Updated',
    field_changed: 'Specific Field(s) Changed'
  };
  return labels[trigger];
}

/**
 * Get action type label
 */
export function getActionTypeLabel(type: WorkflowActionType): string {
  const labels: Record<WorkflowActionType, string> = {
    update_fields: 'Update Fields',
    send_email: 'Send Email',
    create_record: 'Create Record',
    create_task: 'Create Task',
    call_webhook: 'Call Webhook'
  };
  return labels[type];
}

/**
 * Get action type icon
 */
export function getActionTypeIcon(type: WorkflowActionType): any {
  const icons: Record<WorkflowActionType, any> = {
    update_fields: Edit3,
    send_email: Mail,
    create_record: FileText,
    create_task: CheckSquare,
    call_webhook: Webhook
  };
  return icons[type];
}

/**
 * Format condition for display
 */
export function formatCondition(condition: WorkflowCondition): string {
  const operatorLabels: Record<WorkflowCondition['operator'], string> = {
    equals: 'equals',
    notEquals: 'does not equal',
    lessThan: 'less than',
    greaterThan: 'greater than',
    contains: 'contains',
    startsWith: 'starts with',
    isBlank: 'is blank',
    isNotBlank: 'is not blank'
  };

  const needsValue = !['isBlank', 'isNotBlank'].includes(condition.operator);
  const value = needsValue ? ` "${condition.value}"` : '';

  return `${condition.fieldLabel} ${operatorLabels[condition.operator]}${value}`;
}

/**
 * Get execution status color
 */
export function getExecutionStatusColor(status: WorkflowExecution['status']): string {
  const colors: Record<WorkflowExecution['status'], string> = {
    success: 'green',
    failed: 'red',
    partial: 'yellow'
  };
  return colors[status];
}

/**
 * Format duration
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}
